import React, { useState } from 'react';
import {
  Search, Plus, RefreshCw, Check, Copy, Send, Mail, GraduationCap,
  User, Power, PowerOff, Trash2, Upload, MessageSquareHeart, Loader2, FileSpreadsheet, Download,
} from 'lucide-react';
import { toast } from '../../ui/sonner';
import { exportLicensesToExcel } from '../../../utils/licenseExcelExport';
import { InlineOwnerEdit, InlineMaxIpsEdit, IpBadge, InlineMasterEdit, InlineExpiresEdit, InlineStartsEdit, InlineCoSupervisorsEdit } from '../InlineEdits';
import BulkImportDialog from '../BulkImportDialog';
import LicenseFilterBar from './LicenseFilterBar';

/**
 * Admin — Licences tab. Search + filter + table of all licenses.
 * Extracted from AdminPanel.jsx on 29/05/2026 (code review).
 * 21/09/2026 — filtres statut / dates + tri (LicenseFilterBar).
 *
 * Pure presentational. All actions and data come from props.
 */
export default function LicensesTab({
  filtered, licenses, masterLicenses, loading,
  search, setSearch, typeFilter, setTypeFilter, copied,
  hideTest, setHideTest, testCount,
  licFilters, setLicFilters, licSort, setLicSort, statusCounts,
  resendingId,
  token,
  onRefresh, onCreate, onCopyKey, onToggleLicense, onDeleteLicense, onResendWelcome, onLicensesChanged,
}) {
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(null);
  const exportExcel = async (scope) => {
    if (exporting) return;
    setExporting(scope);
    try {
      const list = scope === 'all' ? licenses : filtered;
      const { rows } = await exportLicensesToExcel(list, { allLicenses: licenses, scope });
      toast.success(`${rows} licence${rows > 1 ? 's' : ''} exportée${rows > 1 ? 's' : ''} en Excel`);
    } catch (err) {
      console.error('[admin] license export failed', err);
      toast.error('Export Excel impossible — réessayez.');
    } finally {
      setExporting(null);
    }
  };
  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px] relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, propriétaire ou clé..."
            className="w-full bg-background text-foreground placeholder-slate-500 pl-9 pr-4 py-2.5 border border-ink-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            data-testid="admin-search"
          />
        </div>
        <div className="flex items-center gap-1 border border-ink-600 rounded-lg p-0.5 bg-background">
          <button
            onClick={() => setTypeFilter('all')}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              typeFilter === 'all' ? 'bg-ink-700 text-foreground' : 'text-slate-500 hover:text-slate-200'
            }`}
            data-testid="type-filter-all"
          >
            Tous ({licenses.length})
          </button>
          <button
            onClick={() => setTypeFilter('master')}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              typeFilter === 'master'
                ? 'bg-purple-500/20 text-purple-200 border border-purple-400/30'
                : 'text-slate-500 hover:text-slate-200'
            }`}
            data-testid="type-filter-master"
          >
            <GraduationCap className="w-3 h-3 inline mr-0.5" /> Formateurs ({licenses.filter((l) => l.is_master).length})
          </button>
          <button
            onClick={() => setTypeFilter('user')}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              typeFilter === 'user'
                ? 'bg-accent/15 text-accent border border-accent/30'
                : 'text-slate-500 hover:text-slate-200'
            }`}
            data-testid="type-filter-user"
          >
            <User className="w-3 h-3 inline mr-0.5" /> Apprenants ({licenses.filter((l) => !l.is_master).length})
          </button>
        </div>
        {/* 03/06/2026 — Toggle "Masquer TEST" déplacé dans AdminHeader pour
            qu'il s'applique à tous les onglets admin (pas seulement
            Licences). Pas besoin de checkbox dupliquée ici. */}
        <button onClick={onRefresh} className="p-2.5 border border-ink-600 rounded-lg hover:bg-ink-700/40" data-testid="admin-refresh">
          <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {/* 29/09/2026 — Export Excel (sélection filtrée / tout) */}
        <div className="inline-flex items-center rounded-lg border border-emerald-500/40 overflow-hidden" data-testid="admin-export-group">
          <button
            onClick={() => exportExcel('filtered')}
            disabled={exporting || filtered.length === 0}
            className="flex items-center gap-2 text-emerald-300 px-3 py-2.5 text-sm font-bold hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={`Exporter les ${filtered.length} licences affichées (recherche + filtres + tri) en Excel`}
            data-testid="admin-export-filtered"
          >
            {exporting === 'filtered' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Exporter la sélection ({filtered.length})
          </button>
          <button
            onClick={() => exportExcel('all')}
            disabled={exporting || licenses.length === 0}
            className="flex items-center gap-2 text-emerald-200 px-3 py-2.5 text-sm font-bold border-l border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={`Exporter les ${licenses.length} licences (y compris masquées TEST) en Excel`}
            data-testid="admin-export-all"
          >
            {exporting === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Tout exporter ({licenses.length})
          </button>
        </div>
        <button
          onClick={() => setShowImport(true)}
          className="flex items-center gap-2 border border-accent/50 text-accent px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-accent/10 transition-colors"
          data-testid="admin-bulk-import"
        >
          <Upload className="w-4 h-4" /> Import en masse
        </button>
        <button onClick={onCreate} className="flex items-center gap-2 bg-accent text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-accent-hover transition-colors" data-testid="admin-create-license">
          <Plus className="w-4 h-4" /> Nouvelle licence
        </button>
      </div>

      {showImport && (
        <BulkImportDialog
          token={token}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); onLicensesChanged(); }}
        />
      )}

      <LicenseFilterBar
        filters={licFilters}
        setFilters={setLicFilters}
        sort={licSort}
        setSort={setLicSort}
        statusCounts={statusCounts}
      />
      <p className="text-[11px] text-slate-500 -mt-3" data-testid="license-result-count">
        {filtered.length} licence{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''}
      </p>

      {/* License Table */}
      <div className="bg-ink-800 rounded-xl border border-ink-600 overflow-x-auto">
        <table className="w-full text-sm" data-testid="license-table">
          <thead className="bg-ink-700/30 border-b border-ink-600">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Clé</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Titulaire</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type / Cohorte</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Propriétaire</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Expiration</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Statut</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">IP uniques</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Limite IP</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Activations</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((lic) => (
              <LicenseRow
                key={lic.id}
                lic={lic}
                copied={copied}
                resendingId={resendingId}
                masterLicenses={masterLicenses}
                token={token}
                onCopyKey={onCopyKey}
                onToggleLicense={onToggleLicense}
                onDeleteLicense={onDeleteLicense}
                onResendWelcome={onResendWelcome}
                onLicensesChanged={onLicensesChanged}
              />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500 text-sm" data-testid="license-empty">Aucune licence ne correspond aux filtres</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RexRequestButton({ lic, token }) {
  const [state, setState] = useState('idle'); // idle | busy | done
  const API = process.env.REACT_APP_BACKEND_URL;

  const request = async () => {
    if (state !== 'idle') return;
    const label = lic.is_master
      ? `Demander un REX à TOUS les apprenants de la cohorte de ${lic.holder_name} ? Un email d'invitation sera envoyé à chacun.`
      : `Demander un retour d'expérience (REX) à ${lic.holder_name} ? Un email d'invitation lui sera envoyé.`;
    if (!window.confirm(label)) return;
    setState('busy');
    try {
      const body = lic.is_master ? { master_license_id: lic.id } : { license_ids: [lic.id] };
      const res = await fetch(`${API}/api/admin/rex/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      setState(res.ok ? 'done' : 'idle');
      if (res.ok) setTimeout(() => setState('idle'), 3000);
    } catch {
      setState('idle');
    }
  };

  return (
    <button
      onClick={request}
      disabled={state === 'busy'}
      className={`p-1.5 rounded-lg ${state === 'done' ? 'text-money' : 'text-slate-400 hover:text-accent hover:bg-accent/10'}`}
      title={lic.is_master ? 'Demander un REX à toute la cohorte' : "Demander un REX (retour d'expérience)"}
      data-testid={`rex-request-${lic.id}`}
    >
      {state === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : state === 'done' ? <Check className="w-4 h-4" /> : <MessageSquareHeart className="w-4 h-4" />}
    </button>
  );
}

function LicenseRow({ lic, copied, resendingId, masterLicenses, token, onCopyKey, onToggleLicense, onDeleteLicense, onResendWelcome, onLicensesChanged }) {
  const expired = lic.expires_at && new Date(lic.expires_at) < new Date();
  return (
    <tr
      className={`border-b border-ink-600 transition-colors ${
        lic.is_master
          ? 'bg-purple-500/5 hover:bg-purple-500/10 border-l-2 border-l-purple-400/60'
          : 'hover:bg-ink-700/40'
      }`}
      data-testid={`license-row-${lic.id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <code className="ph-no-capture text-xs font-mono bg-ink-700/40 px-2 py-1 rounded">{lic.license_key}</code>
          <button onClick={() => onCopyKey(lic.license_key)} className="text-slate-500 hover:text-foreground" data-testid={`copy-key-${lic.id}`}>
            {copied === lic.license_key ? <Check className="w-3.5 h-3.5 text-money" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="font-medium text-slate-100">{lic.holder_name}</p>
        {lic.holder_email && <p className="ph-no-capture text-[10px] text-slate-500">{lic.holder_email}</p>}
        {lic.created_at && (
          <p className="text-[10px] text-slate-600" data-testid={`license-created-${lic.id}`}>
            Créée le {new Date(lic.created_at).toLocaleDateString('fr-FR')}
          </p>
        )}
        {lic.contact_email && (
          <p className="text-[10px] text-accent flex items-center gap-1 mt-0.5">
            <Mail className="w-2.5 h-2.5" /> {lic.contact_name || lic.contact_email}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {lic.is_master ? (
          <div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-bold flex items-center gap-1 w-fit" data-testid={`type-master-${lic.id}`}>
              <GraduationCap className="w-3 h-3" /> Responsable
            </span>
            <InlineCoSupervisorsEdit
              licenseId={lic.id}
              current={lic.co_supervisors}
              masters={masterLicenses}
              token={token}
              onSaved={onLicensesChanged}
            />
          </div>
        ) : (
          <InlineMasterEdit licenseId={lic.id} currentMasterId={lic.master_license_id} masterLicenses={masterLicenses} token={token} onSaved={onLicensesChanged} />
        )}
      </td>
      <td className="px-4 py-3">
        <InlineOwnerEdit licenseId={lic.id} currentOwner={lic.owner} token={token} onSaved={onLicensesChanged} />
      </td>
      <td className="px-4 py-3">
        <InlineExpiresEdit
          licenseId={lic.id}
          currentExpiresAt={lic.expires_at}
          token={token}
          onSaved={onLicensesChanged}
        />
        {lic.expires_at && (() => {
          const daysLeft = Math.ceil((new Date(lic.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
          if (daysLeft > 0 && daysLeft <= 30) return <p className="text-[10px] text-warn">{daysLeft}j restants</p>;
          return null;
        })()}
        {!lic.is_master && (
          <InlineStartsEdit
            licenseId={lic.id}
            currentStartsAt={lic.starts_at}
            token={token}
            onSaved={onLicensesChanged}
          />
        )}
      </td>
      <td className="px-4 py-3">
        {!lic.is_active ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-ink-700/40 text-slate-400 font-medium">Désactivée</span>
        ) : expired ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-danger/10 text-danger font-medium">Expirée</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-money/10 text-money font-medium">Active</span>
        )}
      </td>
      <td className="px-4 py-3">
        <IpBadge uniqueIps={lic.unique_ips} maxIps={lic.max_ips} licenseId={lic.id} />
      </td>
      <td className="px-4 py-3">
        <InlineMaxIpsEdit licenseId={lic.id} currentMaxIps={lic.max_ips} token={token} onSaved={onLicensesChanged} />
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-slate-400 font-medium">{lic.activations || 0}</span>
        {lic.last_activated_at && (
          <p className="text-[10px] text-slate-500">{new Date(lic.last_activated_at).toLocaleDateString('fr-FR')}</p>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <RexRequestButton lic={lic} token={token} />
          <button
            onClick={() => onResendWelcome(lic)}
            disabled={resendingId === lic.id}
            className={`p-1.5 rounded-lg disabled:opacity-50 ${
              lic.is_master ? 'text-purple-300 hover:bg-purple-500/10' : 'text-accent hover:bg-accent/10'
            }`}
            title={`Renvoyer l'email de bienvenue${lic.is_master ? ' (Responsable)' : ' (Apprenant)'}`}
            data-testid={`resend-welcome-${lic.id}`}
          >
            {resendingId === lic.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
          <button onClick={() => onToggleLicense(lic)} className={`p-1.5 rounded-lg ${lic.is_active ? 'text-warn hover:bg-warn/10' : 'text-money hover:bg-money/10'}`} title={lic.is_active ? 'Désactiver' : 'Activer'} data-testid={`toggle-license-${lic.id}`}>
            {lic.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
          </button>
          <button onClick={() => onDeleteLicense(lic)} className="p-1.5 rounded-lg text-danger hover:bg-danger/10 hover:text-danger" title="Supprimer" data-testid={`delete-license-${lic.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
